/**
 * What a key is called where a person reads it.
 *
 * The run list said `actionable-sales-growth-v5 · standard` and the run page
 * said `cdd · … · kimi-k3[1m]` — the registry's key and the provider's model
 * id, which are what the system routes on and nothing a deal team reads
 * (#124). Both names already arrive with queries the pages make, so this only
 * looks them up; a key nobody can name is shown as it is rather than hidden.
 */

import { humanise } from "./format";
import type { Options, Playbook } from "./types";

/**
 * A key the registry no longer lists — a retired version, another person's
 * private playbook — is humanised rather than shown raw: the Engagements
 * list read `actionables-sales-growth-v3` in a column of titles.
 */
export function playbookTitle(
  playbooks: Playbook[] | undefined,
  key: string | null | undefined,
): string | null {
  if (!key) return null;
  return playbooks?.find((p) => p.key === key)?.title ?? humanise(key);
}

/**
 * A project's type is a phrase somebody chose ("due diligence") or the key of
 * the playbook whose run registered it — and the second was shown as the key.
 */
export function projectTypeLabel(
  playbooks: Playbook[] | undefined,
  type: string | null | undefined,
): string {
  if (!type) return "—";
  return playbooks?.find((p) => p.key === type)?.title ?? type;
}

export function modelLabel(
  options: Options | undefined,
  provider: string | null | undefined,
  model: string | null | undefined,
): string | null {
  if (!model) return null;
  const providers = options?.providers ?? [];
  const home = providers.find((p) => p.key === provider);
  const found =
    home?.models.find((m) => m.id === model) ??
    providers.flatMap((p) => p.models).find((m) => m.id === model);
  return found?.label ?? model;
}
